import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, Box, Button, Card, CardContent, Chip, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Title } from 'react-admin';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listTemplates, MIN_SEARCH_LENGTH } from '../../lib/payPolicyTemplates/api';
import { rememberListSearch } from './listSearch';
import LoadMoreFooter from './LoadMoreFooter';
import LoadOnScroll from './LoadOnScroll';
import { PAGE_FOOTER_SX } from './pageFooter';
import SearchField from './SearchField';
import { CompanyFilter, CountryFilter } from './TemplateFilters';
import { PayPolicyTemplate, STATUS_COLORS, STATUS_LABELS } from './types';
import { SEARCH_RULES, useCursorList } from './useCursorList';
import VersionsPanel from './VersionsPanel';

export const scopeLabel = (template: Pick<PayPolicyTemplate, 'companyUuids'>) => {
  const count = template.companyUuids?.length ?? 0;
  if (count === 0) return 'Global';
  return count === 1 ? '1 company' : `${count} companies`;
};

/**
 * A family saved as a new version while the list is paged sorts past the cursor and comes again on a
 * later page, maybe renamed by the same save; the higher version takes the earlier row's place.
 */
export const keepNewestPerFamily = (previous: PayPolicyTemplate[], page: PayPolicyTemplate[]): PayPolicyTemplate[] => {
  const merged = [...previous];
  page.forEach((template) => {
    const at = merged.findIndex((listed) => listed.familyUuid === template.familyUuid);
    if (at === -1) merged.push(template);
    else if (template.version > merged[at].version) merged[at] = template;
  });
  return merged;
};

const PayPolicyTemplatesList = () => {
  const navigate = useNavigate();
  // The search and filters live in the URL, so the browser's back button and "All templates" return to the same list.
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('search') ?? '';
  const companyKey = searchParams.get('company') ?? '';
  const countryKey = searchParams.get('country') ?? '';
  const [search, setSearch] = useState(fromUrl);
  // Typing writes the address; the box follows the address, so the menu link and back/forward
  // land on the search they name. Writing it from an effect would put the old search back.
  const writeParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const onSearch = (value: string) => {
    setSearch(value);
    writeParam('search', value);
  };
  useEffect(() => {
    setSearch(fromUrl);
  }, [fromUrl]);
  const query = searchParams.toString();
  useEffect(() => {
    rememberListSearch(query);
  }, [query]);
  const companyUuids = companyKey ? companyKey.split(',') : [];
  const countries = countryKey ? countryKey.split(',') : [];
  const fetchPage = useCallback((name: string, cursor?: string) => listTemplates(name, cursor, { companyUuids: companyKey ? companyKey.split(',') : [], countries: countryKey ? countryKey.split(',') : [] }), [companyKey, countryKey]);
  const { items, total, loading, error, hasMore, autoLoad, loadMore, retry } = useCursorList(fetchPage, search, SEARCH_RULES.templates, keepNewestPerFamily);
  // One row's versions open at a time; opening another closes it.
  const [versionsOf, setVersionsOf] = useState<string | null>(null);

  return (
    <Box sx={{ mt: 2, minWidth: 0 }}>
      <Title title="Pay-Policy Templates" />
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
            <SearchField label="Search by name" value={search} onChange={onSearch} rule={SEARCH_RULES.templates} tooShort={`Type at least ${MIN_SEARCH_LENGTH} letters or digits`} sx={{ flex: '1 1 280px', maxWidth: 420 }} />
            <CompanyFilter value={companyUuids} onChange={(uuids) => writeParam('company', uuids.join(','))} />
            <CountryFilter value={countries} onChange={(codes) => writeParam('country', codes.join(','))} />
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/pay-policy-templates/new')}>
              New template
            </Button>
          </Box>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={retry}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell align="right">Plugins</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((template) => {
                  const open = versionsOf === template.uuid;
                  return (
                    <Fragment key={template.uuid}>
                      <TableRow hover sx={{ cursor: 'pointer', '& > td': open ? { borderBottom: 'none' } : undefined }} onClick={() => navigate(`/pay-policy-templates/${template.uuid}`)}>
                        {/* A word with no spaces wraps instead of widening the table. */}
                        <TableCell sx={{ overflowWrap: 'anywhere' }}>
                          <Typography variant="body2" fontWeight={500}>
                            {template.name}
                          </Typography>
                          {template.description && (
                            <Typography variant="caption" color="text.secondary">
                              {template.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          <Button
                            size="small"
                            variant={open ? 'contained' : 'outlined'}
                            disableElevation
                            endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            aria-label={`Versions of ${template.name}`}
                            aria-expanded={open}
                            onClick={() => setVersionsOf(open ? null : template.uuid)}
                            sx={{ minWidth: 0, px: 1, py: 0, borderRadius: 4, textTransform: 'none' }}
                          >
                            v{template.version}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={STATUS_LABELS[template.status] ?? template.status} color={STATUS_COLORS[template.status] ?? 'default'} />
                        </TableCell>
                        <TableCell>{scopeLabel(template)}</TableCell>
                        <TableCell align="right">{template.plugins.length}</TableCell>
                        <TableCell>{new Date(template.updatedAt).toLocaleDateString()}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(event) => event.stopPropagation()}>
                          <Tooltip title="Edit">
                            <IconButton size="small" aria-label={`Edit ${template.name}`} onClick={() => navigate(`/pay-policy-templates/${template.uuid}`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Start a new template from this one">
                            <IconButton size="small" aria-label={`Start from ${template.name}`} onClick={() => navigate(`/pay-policy-templates/new?from=${template.uuid}`)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {open && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ pt: 0, bgcolor: 'action.hover' }}>
                            <VersionsPanel uuid={template.uuid} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
                {!loading && items.length === 0 && !error && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No templates found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          <LoadOnScroll active={autoLoad} onLoad={loadMore} />
        </CardContent>
      </Card>
      <Box data-testid="list-footer" sx={PAGE_FOOTER_SX}>
        <LoadMoreFooter loaded={items.length} total={total} loading={loading} hasMore={hasMore} onLoadMore={loadMore} />
      </Box>
    </Box>
  );
};

export default PayPolicyTemplatesList;
